import { useState } from 'react';
import { motion } from 'framer-motion';
import { SlideRichTextEditor } from '../SlideRichTextEditor';
import { SettingsButton, SlideSettingsDrawer, DrawerSection, DrawerOption, DrawerCheckbox, DrawerRemove } from '../shared/SlideShared';
import { ImageDropZone } from '../ImageDropZone';
import { FocalPointPicker, focalPos } from '../FocalPointPicker';
import { UnsplashCredit, withImageAttribution } from '../shared/UnsplashCredit';
import {
  TextSlideData,
  TextSlideLayout,
  Theme,
  slideTypographyStyle,
} from '../slideTypes';
import {
  springs,
} from '../../../ui/slide-animations';

export function TextSlide({
  data,
  theme,
  isEditing,
  onUpdate,
}: {
  data: TextSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: TextSlideData) => void;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const layoutLabels: Record<TextSlideLayout, string> = {
    'title': 'Title',
    'body': 'Text',
    'bullets': 'Key Points',
    'two-column': 'Two Columns',
    'image-text': 'Image + Text',
  };

  const settingsDrawer = isEditing && (
    <SlideSettingsDrawer
      isOpen={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      title="Text Slide Settings"
    >
      <DrawerSection label="Layout">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {(Object.keys(layoutLabels) as TextSlideLayout[]).map((layout) => (
            <DrawerOption key={layout} selected={data.layout === layout} onClick={() => onUpdate({ ...data, layout })}>
              {layoutLabels[layout]}
            </DrawerOption>
          ))}
        </div>
      </DrawerSection>

      {/* Alignment (title, body, bullets) */}
      {['title', 'body', 'bullets'].includes(data.layout) && (
        <DrawerSection label="Alignment">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {(['left', 'center', 'right'] as const).map((align) => (
              <DrawerOption
                key={align}
                selected={(data.alignment || (data.layout === 'title' ? 'center' : 'left')) === align}
                onClick={() => onUpdate({ ...data, alignment: align })}
              >
                {align.charAt(0).toUpperCase() + align.slice(1)}
              </DrawerOption>
            ))}
          </div>
        </DrawerSection>
      )}

      {/* Background image (title) */}
      {data.layout === 'title' && (
        <DrawerSection label="Background image">
          <ImageDropZone
            compact
            onImageUrl={(url, attribution) =>
              onUpdate(withImageAttribution({ ...data, backgroundImage: url }, attribution))
            }
            currentUrl={data.backgroundImage}
            primaryColor="var(--accent)"
          />
          {data.backgroundImage && (
            <DrawerRemove
              onClick={() => onUpdate(withImageAttribution({ ...data, backgroundImage: undefined }, undefined))}
            >
              Remove background
            </DrawerRemove>
          )}
          {data.backgroundImage && (
            <div style={{ marginTop: 10 }}>
              <DrawerSection label="Focal point">
                <FocalPointPicker
                  imageUrl={data.backgroundImage}
                  value={data.focalPoint}
                  onChange={(fp) => onUpdate({ ...data, focalPoint: fp })}
                />
              </DrawerSection>
            </div>
          )}
          {data.backgroundImage && (
            <div style={{ marginTop: 10 }}>
              <DrawerCheckbox
                id="text-dark-overlay"
                checked={!!data.darkOverlay}
                onChange={(v) => onUpdate({ ...data, darkOverlay: v })}
                label="Dark overlay on background"
              />
            </div>
          )}
        </DrawerSection>
      )}

      {/* Image settings (image-text) */}
      {data.layout === 'image-text' && (
        <>
          <DrawerSection label="Image">
            <ImageDropZone
              compact
              onImageUrl={(url, attribution) =>
                onUpdate(withImageAttribution({ ...data, imageUrl: url }, attribution))
              }
              currentUrl={data.imageUrl}
              primaryColor="var(--accent)"
            />
          </DrawerSection>
          {data.imageUrl && (
            <DrawerSection label="Focal point">
              <FocalPointPicker
                imageUrl={data.imageUrl}
                value={data.focalPoint}
                onChange={(fp) => onUpdate({ ...data, focalPoint: fp })}
                aspectRatio={(data.imageSize || 'large') === 'landscape' ? '16 / 9' : '3 / 4'}
              />
            </DrawerSection>
          )}
          <DrawerSection label="Image position">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {(['left', 'right'] as const).map((pos) => (
                <DrawerOption
                  key={pos}
                  selected={(data.imagePosition || 'right') === pos}
                  onClick={() => onUpdate({ ...data, imagePosition: pos })}
                >
                  {pos.charAt(0).toUpperCase() + pos.slice(1)}
                </DrawerOption>
              ))}
            </div>
          </DrawerSection>
          <DrawerSection label="Image size">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {([['large', 'Full height'], ['landscape', 'Landscape']] as const).map(([val, label]) => (
                <DrawerOption
                  key={val}
                  selected={(data.imageSize || 'large') === val}
                  onClick={() => onUpdate({ ...data, imageSize: val })}
                >
                  {label}
                </DrawerOption>
              ))}
            </div>
          </DrawerSection>
        </>
      )}

      {/* Column ratio (two-column) */}
      {data.layout === 'two-column' && (
        <DrawerSection label="Column ratio">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {(['50-50', '60-40', '40-60'] as const).map((r) => (
              <DrawerOption key={r} selected={(data.ratio || '50-50') === r} onClick={() => onUpdate({ ...data, ratio: r })}>
                {r}
              </DrawerOption>
            ))}
          </div>
        </DrawerSection>
      )}
    </SlideSettingsDrawer>
  );

  // ---- Layout: Title ----
  if (data.layout === 'title') {
    const alignment = data.alignment || 'center';
    const alignClasses = {
      left: 'text-left items-start',
      center: 'text-center items-center',
      right: 'text-right items-end',
    };
    const overlayActive = !!(data.backgroundImage && data.darkOverlay);
    const titleColor = overlayActive ? '#ffffff' : theme.colors.text;
    const subtitleColor = overlayActive ? 'rgba(255,255,255,0.85)' : theme.colors.textMuted;

    return (
      <div
        className={`relative h-full flex flex-col group ${isEditing ? 'justify-center' : 'justify-start'} ${alignClasses[alignment]}`}
        style={{
          padding: isEditing ? '2rem 4rem' : theme.spacing.padded,
          paddingTop: isEditing ? undefined : `max(15%, calc(var(--slide-chrome-top, 0px) + 12px))`,
          backgroundImage: data.backgroundImage ? `url(${data.backgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: focalPos(data.focalPoint),
        }}
      >
        {overlayActive && (
          <div className="absolute inset-0 bg-black/50 pointer-events-none" />
        )}
        {isEditing ? (
          <div className="relative z-10 w-full">
            <SlideRichTextEditor
              value={data.heading || ''}
              onChange={(html) => onUpdate({ ...data, heading: html })}
              placeholder="Enter headline..."
              theme={theme}
              variant="heading"
              textStyle={{
                ...slideTypographyStyle(theme, 'title'),
                color: titleColor,
              }}
            />
            <div className="mt-4">
              <SlideRichTextEditor
                value={data.body || ''}
                onChange={(html) => onUpdate({ ...data, body: html })}
                placeholder="Enter subtitle (optional)..."
                theme={theme}
                variant="heading"
                textStyle={{
                  ...slideTypographyStyle(theme, 'subheading', theme.fonts.body),
                  color: subtitleColor,
                }}
              />
            </div>
          </div>
        ) : (
          <div className={`relative z-10 slide-content-wide ${alignment === 'center' ? 'mx-auto' : alignment === 'right' ? 'ml-auto' : ''}`}>
            {data.heading && (
              <motion.div
                className="slide-rich-content"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={springs.gentle as object}
                data-slide-level="title"
                style={{
                  ...slideTypographyStyle(theme, 'title'),
                  color: titleColor,
                }}
                dangerouslySetInnerHTML={{ __html: data.heading }}
              />
            )}
            {data.body && (
              <motion.div
                className="slide-content-narrow slide-rich-content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...(springs.gentle as object), delay: 0.25 }}
                style={{
                  ...slideTypographyStyle(theme, 'subheading', theme.fonts.body),
                  color: subtitleColor,
                }}
                dangerouslySetInnerHTML={{ __html: data.body }}
              />
            )}
          </div>
        )}
        <UnsplashCredit author={data.unsplashAuthor} authorUrl={data.unsplashAuthorUrl} />
        {isEditing && <SettingsButton onClick={() => setSettingsOpen(true)} />}
        {settingsDrawer}
      </div>
    );
  }

  // ---- Layout: Body / Bullets ----
  if (data.layout === 'body' || data.layout === 'bullets') {
    const alignment = data.alignment || 'left';
    const alignClasses = {
      left: 'text-left items-start',
      center: 'text-center items-center',
      right: 'text-right items-end',
    };

    return (
      <div
        className={`relative h-full group ${isEditing ? '' : ''}`}
        style={{ backgroundColor: theme.colors.background }}
      >
        <div
          className={`h-full flex flex-col justify-center ${alignClasses[alignment]}`}
          style={{ padding: theme.spacing.padded, paddingTop: `max(${theme.spacing.padded}, calc(var(--slide-chrome-top, 0px) + 12px))` }}
        >
          {isEditing ? (
            <div className="w-full" style={{ textAlign: alignment }}>
              <SlideRichTextEditor
                value={data.heading || ''}
                onChange={(html) => onUpdate({ ...data, heading: html })}
                placeholder="Title"
                theme={theme}
                variant="heading"
                textStyle={{
                  ...slideTypographyStyle(theme, 'heading'),
                  color: theme.colors.text,
                  textAlign: alignment,
                }}
              />
              <div className="mt-6">
                <SlideRichTextEditor
                  value={data.body || ''}
                  onChange={(html) => onUpdate({ ...data, body: html })}
                  placeholder="Add your content"
                  theme={theme}
                  variant="body"
                  textStyle={{
                    ...slideTypographyStyle(theme, 'body', theme.fonts.body),
                    color: theme.colors.text,
                    textAlign: alignment,
                  }}
                />
              </div>
            </div>
          ) : (
            <div className={`slide-content-medium ${alignment === 'center' ? 'mx-auto' : alignment === 'right' ? 'ml-auto' : ''}`} style={{ textAlign: alignment }}>
              {data.heading && (
                <motion.div
                  className="mb-3 slide-rich-content"
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={springs.gentle as object}
                  data-slide-level="heading"
                  style={{
                    ...slideTypographyStyle(theme, 'heading'),
                    color: theme.colors.text,
                  }}
                  dangerouslySetInnerHTML={{ __html: data.heading }}
                />
              )}
              {data.heading && (
                <motion.div
                  className="mb-8 h-0.5 rounded-full"
                  style={{
                    backgroundColor: theme.colors.primary,
                    width: alignment === 'center' ? '3rem' : '2.5rem',
                    marginLeft: alignment === 'center' || alignment === 'right' ? 'auto' : undefined,
                    marginRight: alignment === 'center' || alignment === 'left' ? 'auto' : undefined,
                  }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ ...(springs.decorative as object), delay: 0.12 }}
                />
              )}
              <motion.div
                className="slide-rich-content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...(springs.gentle as object), delay: 0.2 }}
                style={{
                  ...slideTypographyStyle(theme, 'body', theme.fonts.body),
                  color: theme.colors.text,
                }}
                dangerouslySetInnerHTML={{ __html: data.body || '' }}
              />
            </div>
          )}
        </div>
        {isEditing && <SettingsButton onClick={() => setSettingsOpen(true)} />}
        {settingsDrawer}
      </div>
    );
  }

  // ---- Layout: Two Column ----
  if (data.layout === 'two-column') {
    const ratio = data.ratio || '50-50';
    const leftWidth = ratio === '60-40' ? 'slide-col-60' : ratio === '40-60' ? 'slide-col-40' : 'slide-col-half';
    const rightWidth = ratio === '60-40' ? 'slide-col-40' : ratio === '40-60' ? 'slide-col-60' : 'slide-col-half';

    const bodyStyle = {
      ...slideTypographyStyle(theme, 'body', theme.fonts.body),
      color: theme.colors.text,
    };

    return (
      <div className="relative h-full slide-columns group">
        <motion.div
          className={`flex-1 ${leftWidth} flex flex-col justify-center`}
          style={{
            backgroundColor: theme.colors.surface,
            paddingInline: theme.spacing.insetX,
            paddingBlock: theme.spacing.padded,
            paddingTop: `max(${theme.spacing.padded}, calc(var(--slide-chrome-top, 0px) + 12px))`,
          }}
          initial={!isEditing ? { opacity: 0, x: -50 } : false}
          animate={{ opacity: 1, x: 0 }}
          transition={springs.gentle as object}
        >
          {isEditing ? (
            <SlideRichTextEditor
              value={data.body || ''}
              onChange={(html) => onUpdate({ ...data, body: html })}
              placeholder="Left column content..."
              theme={theme}
              textStyle={bodyStyle}
            />
          ) : (
            <motion.div
              className="slide-rich-content"
              style={bodyStyle}
              dangerouslySetInnerHTML={{ __html: data.body || '' }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...(springs.gentle as object), delay: 0.1 }}
            />
          )}
        </motion.div>
        {/* Vertical accent separator */}
        {!isEditing && (
          <motion.div
            className="slide-separator w-px self-stretch my-12"
            style={{ backgroundColor: theme.colors.primary + '30' }}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ ...(springs.decorative as object), delay: 0.2 }}
          />
        )}
        <motion.div
          className={`flex-1 ${rightWidth} flex flex-col justify-center`}
          style={{ backgroundColor: theme.colors.background, paddingInline: theme.spacing.insetX, paddingBlock: theme.spacing.padded }}
          initial={!isEditing ? { opacity: 0, x: 50 } : false}
          animate={{ opacity: 1, x: 0 }}
          transition={springs.gentle as object}
        >
          {isEditing ? (
            <SlideRichTextEditor
              value={data.secondaryBody || ''}
              onChange={(html) => onUpdate({ ...data, secondaryBody: html })}
              placeholder="Right column content..."
              theme={theme}
              textStyle={bodyStyle}
            />
          ) : (
            <motion.div
              className="slide-rich-content"
              style={bodyStyle}
              dangerouslySetInnerHTML={{ __html: data.secondaryBody || '' }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...(springs.gentle as object), delay: 0.15 }}
            />
          )}
        </motion.div>
        {isEditing && <SettingsButton onClick={() => setSettingsOpen(true)} />}
        {settingsDrawer}
      </div>
    );
  }

  // ---- Layout: Image + Text ----
  if (data.layout === 'image-text') {
    const imagePosition = data.imagePosition || 'right';
    const imageSize = data.imageSize || 'large';
    const imageOrderClass = !isEditing && imagePosition === 'right' ? 'order-last' : '';

    const imageColumn = (
      <div
        className={`relative flex-shrink-0 overflow-hidden slide-img-col ${imageOrderClass} ${
          isEditing
            // No h-full here: an explicit percentage height disables flex
            // stretch, and under the grow-with-content canvas it resolves to
            // auto anyway. Stretch sizes the column to the row (≥ one frame
            // via the root's min-height), which the img's h-full then fills.
            ? (imageSize === 'large' ? 'w-1/2' : 'w-1/2 flex items-center justify-center')
            : (imageSize === 'large'
                ? ''
                : 'flex items-center justify-center')
        }`}
        style={
          isEditing && imageSize === 'large'
            // Flex stretch can't reach the root's min-height (a single flex
            // line sizes to content), so pin the column to exactly one frame:
            // the editor's first frame then matches the student view's crop.
            ? { height: '100cqh' }
            : !isEditing && imageSize !== 'large' ? { padding: theme.spacing.compact } : undefined
        }
      >
        {data.imageUrl ? (
          !isEditing ? (
            <motion.div
              className={imageSize === 'large' ? 'w-full h-full' : 'w-full relative'}
              style={imageSize !== 'large' ? { paddingBottom: '56.25%' } : undefined}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={springs.smooth as object}
            >
              <img
                src={data.imageUrl}
                alt=""
                className={imageSize === 'large'
                  ? 'w-full h-full object-cover'
                  : 'absolute inset-0 w-full h-full object-cover rounded-xl shadow-lg'
                }
                style={{ objectPosition: focalPos(data.focalPoint) }}
              />
            </motion.div>
          ) : imageSize === 'large' ? (
            <img
              src={data.imageUrl}
              alt=""
              className="w-full h-full object-cover"
              style={{ objectPosition: focalPos(data.focalPoint) }}
            />
          ) : (
            <div className="w-full relative" style={{ paddingBottom: '56.25%' }}>
              <img
                src={data.imageUrl}
                alt=""
                className="absolute inset-0 w-full h-full object-cover rounded-xl shadow-lg"
                style={{ objectPosition: focalPos(data.focalPoint) }}
              />
            </div>
          )
        ) : isEditing ? (
          <ImageDropZone
            onImageUrl={(url, attribution) =>
              onUpdate(withImageAttribution({ ...data, imageUrl: url }, attribution))
            }
            className={imageSize === 'large' ? 'w-full h-full' : 'w-full aspect-video'}
          />
        ) : (
          <div
            className={`flex items-center justify-center rounded-xl ${
              imageSize === 'large' ? 'w-full h-full' : 'w-full aspect-video'
            }`}
            style={{ backgroundColor: theme.colors.primary + '08' }}
          />
        )}
      </div>
    );

    const textColumn = (
      <div
        className={`flex-1 flex flex-col justify-center${isEditing ? ' overflow-hidden' : ''}`}
        style={{
          paddingInline: theme.spacing.insetX,
          paddingBlock: theme.spacing.compact,
          paddingTop: `max(${theme.spacing.compact}, calc(var(--slide-chrome-top, 0px) + 12px))`,
        }}
      >
        {isEditing ? (
          <>
            <SlideRichTextEditor
              value={data.heading || ''}
              onChange={(html) => onUpdate({ ...data, heading: html })}
              placeholder="Heading (optional)..."
              theme={theme}
              variant="heading"
              textStyle={{
                ...slideTypographyStyle(theme, 'heading'),
                color: theme.colors.text,
              }}
            />
            <div className="mt-3">
              <SlideRichTextEditor
                value={data.body || ''}
                onChange={(html) => onUpdate({ ...data, body: html })}
                placeholder="Enter your text..."
                theme={theme}
                variant="body"
                textStyle={{
                  ...slideTypographyStyle(theme, 'body', theme.fonts.body),
                  color: theme.colors.text,
                }}
              />
            </div>
          </>
        ) : (
          <>
            {data.heading && (
              <motion.div
                className="font-bold mb-2 slide-rich-content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={springs.gentle as object}
                data-slide-level="heading"
                style={{
                  ...slideTypographyStyle(theme, 'heading'),
                  color: theme.colors.text,
                }}
                dangerouslySetInnerHTML={{ __html: data.heading }}
              />
            )}
            {data.heading && (
              <motion.div
                className="mb-5 h-0.5 rounded-full"
                style={{ backgroundColor: theme.colors.primary, width: '2rem' }}
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ ...(springs.decorative as object), delay: 0.12 }}
              />
            )}
            <motion.div
              className="slide-rich-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              style={{
                ...slideTypographyStyle(theme, 'body', theme.fonts.body),
                color: theme.colors.text,
              }}
              dangerouslySetInnerHTML={{ __html: data.body || '' }}
            />
          </>
        )}
      </div>
    );

    return (
      <div
        className={`relative h-full group ${isEditing ? 'flex overflow-hidden' : 'slide-columns'}`}
        style={{
          backgroundColor: theme.colors.background,
          // Editor parity: under the canvas's grow-with-content wrapper the
          // percentage-height chain is auto, so the image column would render
          // at its natural aspect height instead of filling the frame like the
          // student view. 100cqh = the slide container's definite height, so
          // the row is at least one full frame tall; flex stretch then sizes
          // the image column to the row (taller if long text grows it).
          ...(isEditing ? { minHeight: '100cqh' } : {}),
        }}
      >
        {imagePosition === 'left' ? (
          <>{imageColumn}{textColumn}</>
        ) : (
          <>{textColumn}{imageColumn}</>
        )}
        <UnsplashCredit author={data.unsplashAuthor} authorUrl={data.unsplashAuthorUrl} />
        {isEditing && <SettingsButton onClick={() => setSettingsOpen(true)} />}
        {settingsDrawer}
      </div>
    );
  }

  // Fallback
  return (
    <div className="h-full flex items-center justify-center" style={{ color: theme.colors.textMuted }}>
      Unknown text layout
    </div>
  );
}
